import { useContext, useEffect, useState } from "react";
import { HelpAndResourcesButton } from "../components/v2/HelpAndResourcesButton";
import { GuidedTourTooltipProps } from "../components/v2/GuidedTourTooltip";
import { HelpAndResourcesOptions } from "../enums";
import { CourseStructureCtx } from "../interfaces";
import { CourseStructureContext } from "../context/CourseStructureContext";

interface UseHelpAndResourcesProps {
  hideHelpAndResourcesOption?: HelpAndResourcesOptions[];
  toolTipProps?: Omit<
    GuidedTourTooltipProps,
    "currentStep" | "setCurrentStep" | "parentMenuOpen"
  >;
  yOffset?: number | string;
  showReminder?: boolean;
  sampleProblem?: boolean;
  showModal?: boolean;
  // these are used for intercepting the tour and redirecting to another page, like sample problem
  onStartTour?: () => void; // use a callback instead of opening the tour
  onEndTOur?: () => void; // use a callback instead of closing the tour
  hideButton?: boolean;
}

export function useHelpAndResources(props: UseHelpAndResourcesProps) {
  const [guidedTourOpen, setGuidedTourOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const { showReminder = false, hideHelpAndResourcesOption = [], hideButton } = props;
  const [hideOptions, setHideOptions] = useState<HelpAndResourcesOptions[]>(
    hideHelpAndResourcesOption
  );
  const [showReminderNudge, setShowReminderNudge] = useState<boolean>(showReminder);

  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const metadata = courseStructureCtx.courseTreeStructure?.getCurrentCourseMetadata();
  const currentCourse = courseStructureCtx.courseTreeStructure?.getCurrentCourse();

  useEffect(() => {
    if (!courseStructureCtx.courseTreeStructure) return;

    if (metadata?.help_and_resource_button?.disable_guided_tour) {
      setHideOptions((prev) => [...prev, HelpAndResourcesOptions.GUIDED_TOUR]);
      setShowReminderNudge(false);
    }
    if (metadata?.help_and_resource_button?.disable_feedback_and_help) {
      setHideOptions((prev) => [...prev, HelpAndResourcesOptions.CONTACT_TECH_SUPPORT]);
    }
    if (metadata?.help_and_resource_button?.disable_tos) {
      setHideOptions((prev) => [...prev, HelpAndResourcesOptions.TOS]);
    }
    if (metadata?.help_and_resource_button?.disable_accessibility) {
      setHideOptions((prev) => [...prev, HelpAndResourcesOptions.ACCESSIBILITY]);
    }

    if (
      metadata?.help_and_resource_button?.disable_guided_tour &&
      metadata?.help_and_resource_button?.disable_feedback_and_help &&
      metadata?.help_and_resource_button?.disable_tos &&
      metadata?.help_and_resource_button?.disable_accessibility
    ) {
      setHideOptions((prev) => [...prev, HelpAndResourcesOptions.ALL]);
    }
  }, [courseStructureCtx, metadata]);

  const handleCloseGuidedTour = () => {
    if (props.onEndTOur) {
      props.onEndTOur();
    } else {
      setGuidedTourOpen(false);
      setCurrentStep(0);
    }
  };

  const handleOpenGuidedTour = () => {
    if (props.onStartTour) {
      props.onStartTour();
    } else {
      setGuidedTourOpen(true);
      setCurrentStep(0);
    }
  };

  let element;
  if (hideOptions.includes(HelpAndResourcesOptions.ALL)) {
    element = <></>;
  } else {
    element = (
      <HelpAndResourcesButton
        hideButton={hideButton}
        hideOptions={hideOptions}
        yOffset={props.yOffset}
        guidedTourProps={props.toolTipProps}
        guidedTourOpen={guidedTourOpen}
        closeGuidedTour={handleCloseGuidedTour}
        openGuidedTour={handleOpenGuidedTour}
        showReminder={showReminderNudge}
        sampleProblem={props.sampleProblem && guidedTourOpen}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        showModal={props.showModal}
        higherEdCourse={currentCourse?.higher_ed}
      />
    );
  }

  return {
    element,
    currentStep,
    guidedTourOpen,
    setGuidedTourOpen,
    handleCloseGuidedTour,
    handleOpenGuidedTour,
  };
}
